import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, RotateCcw } from 'lucide-react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
} from 'recharts';
import { api } from '../../services/api';
import { fmtMoney, fmtMoneyShort, fmtNumber, fmtPct } from './format';
import OtherDetail from './OtherDetail';
import { c, card, chart } from '../../theme';

/**
 * RankedBarChart — magnitude comparison for one nominal dimension.
 *
 * Form: horizontal bars. The job is "compare magnitude, low → high" and the
 * labels are long ("Computer Equipment/Software Expense"), which is exactly
 * what horizontal bars are for.
 *
 * Colour: ONE hue for every bar. Spend categories and expense items are
 * nominal — they have no natural order — so shading bars darker-where-bigger
 * would double-encode the length as hue and spend the only free channel on
 * information the bar already shows. Magnitude is carried by length alone.
 *
 * The long tail folds into a single "Other" bar rather than being silently
 * dropped, so what is on screen still adds up to the filtered total.
 */
export default function RankedBarChart({
  title, subtitle, dimension, filterParams, topN = 10, onDrill, height = 300,
  excludeLabels = [], mutedLabels = [], unitLabel = 'values', step = 15,
  // What the excluded bucket IS, in words, for the footer. The excluded amount
  // is stated there rather than left implicit, so a chart that covers a slice
  // of the filtered total never reads as if it covered all of it.
  excludeNote = 'excluded from this chart',
}) {
  // "Other" is not a rounding error — on the vendor dimension it is 55% of the
  // chart, dwarfing every named bar beside it. Two ways out of that: pull more
  // bars onto the chart (`extra`), or open the full itemized list (`detail`).
  // Both exist because they answer different questions — "who else is big?" vs
  // "what exactly is in there?".
  const [extra, setExtra]   = useState(0);
  const [detail, setDetail] = useState(false);
  const effectiveTopN = Math.min(topN + extra, 60);

  // Reset the expansion when the filter or dimension changes — otherwise a
  // narrowed selection would silently keep an expansion that no longer suits it.
  useEffect(() => { setExtra(0); }, [dimension, JSON.stringify(filterParams)]);
  // Only the top N are requested. The "Other" bar is derived from the server's
  // scope total rather than by summing the returned tail — on the vendor
  // dimension there are 499+ distinct values, so any client-side tail sum
  // silently omits everything past the row cap (it understated by $291,034).
  const { data, isFetching } = useQuery({
    queryKey: ['expense-ranked', dimension, filterParams, effectiveTopN, excludeLabels],
    queryFn:  () => api.get('/expenses/breakdown', {
      params: { ...filterParams, dimension, limit: effectiveTopN,
                ...(excludeLabels.length ? { exclude: excludeLabels.join(',') } : {}) },
    }),
    keepPreviousData: true,
  });

  const scope = data?.scope;
  const top   = data?.rows || [];
  const scopeTotal     = scope?.net_amount ?? 0;
  const excludedAmount = scope?.excluded_amount ?? 0;

  const topSum   = top.reduce((a, r) => a + r.net_amount, 0);
  const topTxns  = top.reduce((a, r) => a + r.transaction_count, 0);
  const otherAmt = Number((scopeTotal - topSum).toFixed(2));
  const otherCnt = (scope?.transaction_count ?? 0) - topTxns;
  const otherN   = data?.remainder_count ?? 0;

  const muted = new Set(mutedLabels);
  const chartData = [
    ...top.map((r) => ({ ...r, name: r.label, isOther: false, isMuted: muted.has(r.label) })),
    // Named for what it actually contains, not just "Other" — the count and the
    // unit make it obvious this is a tail of real values, never a blank bucket.
    ...(otherN > 0
      ? [{ name: `+ ${fmtNumber(otherN)} smaller ${unitLabel}`,
           net_amount: otherAmt, transaction_count: otherCnt,
           share: scopeTotal ? otherAmt / scopeTotal : 0, isOther: true }]
      : []),
  // Data order IS top-to-bottom on a Recharts vertical-layout category axis, so
  // the rows stay in descending order: biggest at the top where the eye starts,
  // "Other" last. (An earlier .reverse() here assumed the axis was drawn
  // bottom-up and put the largest bar at the BOTTOM, which read backwards.)
  ];

  if (!isFetching && top.length === 0) {
    return (
      <div style={s.card}>
        <div style={s.title}>{title}</div>
        <div style={s.empty}>No transactions match these filters.</div>
      </div>
    );
  }

  return (
    <div style={{ ...s.card, opacity: isFetching ? 0.6 : 1 }}>
      {/* Actions live in the header, not under the plot. Reaching a control
          below the chart means dragging the cursor across the plot area with
          the tooltip trailing it, which made the button a moving target. */}
      <div style={s.head}>
        <div style={{ minWidth: 0 }}>
          <div style={s.title}>{title}</div>
          <div style={s.subtitle}>
            {subtitle}
            {otherN > 0 && ` · ${fmtNumber(otherN)} smaller grouped as Other`}
          </div>
        </div>
        {otherN > 0 && (
          <div style={s.headActions}>
            {effectiveTopN < 60 && (
              <button type="button" style={s.miniBtn} onClick={() => setExtra((x) => x + step)}>
                <ChevronDown size={11} /> Show {step} more
              </button>
            )}
            {extra > 0 && (
              <button type="button" style={s.miniBtn} onClick={() => setExtra(0)}>
                <RotateCcw size={11} /> Reset
              </button>
            )}
            <button type="button" style={s.primaryMini} onClick={() => setDetail(true)}>
              Itemize all {fmtNumber(otherN)}
            </button>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={Math.max(height, chartData.length * 26 + 40)}>
        <BarChart data={chartData} layout="vertical"
                  margin={{ top: 4, right: 74, bottom: 4, left: 4 }}
                  onClick={(state) => {
                    // Recharts 3 does NOT pass activePayload to a chart-level
                    // onClick — the handler receives only activeIndex /
                    // activeLabel / activeCoordinate / isTooltipActive. The old
                    // v2 `e.activePayload[0].payload` read undefined here and
                    // the handler returned silently, so every bar on every
                    // ranked chart looked clickable (the tooltip even says
                    // "Click to filter") and did nothing. Resolve the row from
                    // our own data instead, by index, falling back to matching
                    // the label.
                    const i = Number(state?.activeIndex);
                    const p = Number.isInteger(i) && i >= 0 && i < chartData.length
                      ? chartData[i]
                      : chartData.find((d) => d.name === state?.activeLabel);
                    if (!p) return;
                    // Clicking Other opens its contents; clicking a real value
                    // filters to it.
                    if (p.isOther) setDetail(true);
                    else if (onDrill) onDrill(dimension, p.name);
                  }}>
          {/* Hairline, solid, one shade off the surface — never dashed. */}
          <CartesianGrid horizontal={false} stroke={chart.grid} />
          <XAxis type="number" tickFormatter={fmtMoneyShort}
                 tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" width={168}
                 tick={{ fontSize: 10.5, fill: '#6B7280' }} axisLine={false} tickLine={false}
                 interval={0} />
          <Tooltip
            wrapperStyle={{ pointerEvents: 'none' }}
            isAnimationActive={false}
            cursor={{ fill: 'rgba(27,42,74,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload;
              return (
                <div style={s.tip}>
                  <div style={s.tipName}>{p.name}</div>
                  <div style={s.tipVal}>{fmtMoney(p.net_amount)}</div>
                  <div style={s.tipMeta}>
                    {fmtNumber(p.transaction_count)} transactions · {fmtPct(p.share)} of selection
                  </div>
                  <div style={s.tipHint}>
                    {p.isOther ? 'Click to see everything inside this bar' : onDrill ? 'Click to filter' : ''}
                  </div>
                </div>
              );
            }}
          />
          <Bar dataKey="net_amount" fill={chart.emphasis} radius={[0, 4, 4, 0]}
               barSize={14} cursor={onDrill ? 'pointer' : 'default'}>
            {/* Muted bars are real data that is NOT a member of the dimension —
                the reimbursement bucket on a vendor chart, or <Unmapped> on a
                category chart. Greyed so it reads as "not one of these" at a
                glance, but still charted and still counted, rather than being
                excluded or quietly folded into Other. */}
            {chartData.map((d) => (
              <Cell key={d.name}
                    fill={d.isMuted ? chart.deselected : d.isOther ? chart.recessive : chart.emphasis} />
            ))}
            {/* ~10 bars, so a value at each end stays readable rather than
                becoming noise — and it makes the chart legible without hover. */}
            <LabelList dataKey="net_amount" position="right"
                       formatter={fmtMoneyShort}
                       style={{ fontSize: 10, fill: '#6B7280', fontVariantNumeric: 'tabular-nums' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Magnitude stated in words. Text only — the controls for it are in the
          header, clear of the plot. */}
      {otherN > 0 && (
        <div style={s.otherBar}>
          <span style={otherAmt / (scopeTotal || 1) > 0.25 ? s.otherWarn : s.otherNote}>
            “Other” holds {fmtMoney(otherAmt)} across {fmtNumber(otherN)} {unitLabel}
            {' '}({fmtPct(otherAmt / (scopeTotal || 1), 0)} of this chart) — click the bar to itemize it
          </span>
        </div>
      )}

      {scope && (
        <div style={s.footer}>
          <span>
            {fmtNumber(scope.distinct_count)} {scope.distinct_count === 1 ? 'value' : unitLabel}
            {' · '}{fmtNumber(scope.transaction_count)} transactions
            {excludedAmount !== 0 && (
              <span style={s.excl}> · excludes {fmtMoney(excludedAmount)} {excludeNote}</span>
            )}
          </span>
          <span style={s.footerAmt}>{fmtMoney(scopeTotal)}</span>
        </div>
      )}

      <OtherDetail
        open={detail} onClose={() => setDetail(false)}
        dimension={dimension} dimensionLabel={title.replace(/^Spend by\s*/i, '')}
        filterParams={filterParams} headCount={top.length}
        unitLabel={unitLabel} totalAmount={otherAmt} totalCount={otherN}
        onDrill={onDrill} />
    </div>
  );
}

const s = {
  card:     { ...card.sm, padding: '14px 16px 8px', transition: 'opacity 0.15s' },
  head:     { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              gap: '12px', marginBottom: '10px', flexWrap: 'wrap' },
  headActions: { display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 },
  title:    { fontSize: '13px', fontWeight: 600, color: '#111827' },
  subtitle: { fontSize: '11px', color: '#9CA3AF', marginTop: '2px' },
  empty:    { padding: '48px 8px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' },
  tip:      { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 10px', boxShadow: '0 4px 14px rgba(0,0,0,0.08)' },
  tipName:  { fontSize: '11.5px', fontWeight: 600, color: '#111827', marginBottom: '2px', maxWidth: '260px' },
  tipVal:   { fontSize: '14px', fontWeight: 700, color: c.navy, fontVariantNumeric: 'tabular-nums' },
  tipMeta:  { fontSize: '10.5px', color: '#6B7280', marginTop: '2px' },
  tipHint:  { fontSize: '10px', color: '#1D4ED8', marginTop: '4px' },
  footer:   { display: 'flex', justifyContent: 'space-between', marginTop: '6px', paddingTop: '8px', borderTop: '1px solid #F3F4F6', fontSize: '11px', color: '#6B7280' },
  footerAmt:{ fontWeight: 600, color: '#374151', fontVariantNumeric: 'tabular-nums' },
  excl:     { color: '#B45309' },
  otherBar: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
              marginTop: '8px', paddingTop: '9px', borderTop: '1px dashed #E5E7EB' },
  otherNote:{ fontSize: '11px', color: '#6B7280' },
  otherWarn:{ fontSize: '11px', color: '#92400E', fontWeight: 500 },
  miniBtn:  { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px',
              border: '1px solid #E5E7EB', borderRadius: '5px', background: '#fff',
              fontSize: '10.5px', color: '#374151', cursor: 'pointer', fontFamily: 'inherit' },
  primaryMini: { padding: '3px 10px', border: 'none', borderRadius: '5px', background: c.navy,
                 color: '#fff', fontSize: '10.5px', cursor: 'pointer', fontFamily: 'inherit' },
};
